<template>
  <v-dialog :model-value="dialogOpen" max-width="520" persistent>
    <v-card rounded="lg" class="reminder-card">
      <v-card-title class="d-flex align-center">
        <v-icon color="orange" class="mr-2">mdi-bell-ring-outline</v-icon>
        <span>Erinnerung</span>
        <v-spacer />
        <v-chip v-if="reminders.length > 1" size="small" color="orange" variant="tonal">
          {{ currentIndex + 1 }} / {{ reminders.length }}
        </v-chip>
      </v-card-title>

      <v-card-text v-if="currentReminder">
        <div class="text-h6 reminder-title">
          {{ reminderTitle(currentReminder) }}
        </div>
        <div class="text-body-2 text-medium-emphasis mt-1">
          {{ reminderDateTime(currentReminder) }}
        </div>
        <div v-if="reminderType(currentReminder)" class="mt-2">
          <v-chip size="small" variant="tonal">
            {{ reminderType(currentReminder) }}
          </v-chip>
        </div>
        <div v-if="reminderNote(currentReminder)" class="text-body-2 mt-4 reminder-note">
          {{ reminderNote(currentReminder) }}
        </div>

        <v-select
          v-model="selectedSnoozeMinutes"
          :items="snoozeOptions"
          item-title="text"
          item-value="value"
          label="Zurückstellen um"
          density="compact"
          class="mt-5"
          hide-details
        />
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="reminders.length > 1"
          variant="text"
          :disabled="busy"
          @click="previousReminder"
        >
          Zurück
        </v-btn>
        <v-btn
          v-if="reminders.length > 1"
          variant="text"
          :disabled="busy"
          @click="nextReminder"
        >
          Weiter
        </v-btn>
        <v-spacer />
        <v-btn color="primary" variant="tonal" :loading="busy" @click="snoozeCurrent">
          Erneut erinnern
        </v-btn>
        <v-btn color="primary" variant="text" :loading="busy" @click="dismissCurrent">
          Schließen
        </v-btn>
        <v-btn
          v-if="reminders.length > 1"
          color="primary"
          variant="text"
          :loading="busy"
          @click="dismissAll"
        >
          Alle schließen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';

function firstDefined(source, keys) {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) return source[key];
  }
  return undefined;
}

export default {
  name: 'ReminderPopup',
  data() {
    return {
      currentIndex: 0,
      selectedSnoozeMinutes: 60,
      busy: false,
      snoozeOptions: [
        { text: '1 Stunde', value: 60 },
        { text: '1 Tag', value: 1440 },
        { text: '3 Tage', value: 4320 },
        { text: '1 Woche', value: 10080 },
        { text: '2 Wochen', value: 20160 },
        { text: '2 Monate', value: 86400 },
      ],
    };
  },
  computed: {
    ...mapGetters({
      reminders: 'stateDueReminders',
    }),
    dialogOpen() {
      return Array.isArray(this.reminders) && this.reminders.length > 0;
    },
    currentReminder() {
      const list = Array.isArray(this.reminders) ? this.reminders : [];
      return list[this.currentIndex] || list[0] || null;
    },
  },
  watch: {
    reminders() {
      const list = Array.isArray(this.reminders) ? this.reminders : [];
      if (this.currentIndex >= list.length) this.currentIndex = Math.max(0, list.length - 1);
    },
  },
  methods: {
    reminderId(reminder) {
      return firstDefined(reminder, ['idcalender_entry', 'id', 'calender_entry_id']);
    },
    reminderTitle(reminder) {
      return firstDefined(reminder, ['calender_name', 'name', 'title']) || 'Kalendereintrag';
    },
    reminderNote(reminder) {
      return firstDefined(reminder, ['calender_note', 'note']) || '';
    },
    reminderType(reminder) {
      return firstDefined(reminder, ['calender_event_type', 'event_type']) || '';
    },
    reminderDateTime(reminder) {
      const date = String(firstDefined(reminder, ['calender_start_date', 'start_date', 'date']) || '').slice(0, 10);
      const time = String(firstDefined(reminder, ['calender_start_time', 'start_time', 'time']) || '').slice(0, 5);
      if (!date) return '';

      const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
      const formattedDate = match ? `${match[3]}.${match[2]}.${match[1]}` : date;
      return time ? `${formattedDate} ${time}` : formattedDate;
    },
    previousReminder() {
      const list = Array.isArray(this.reminders) ? this.reminders : [];
      if (!list.length) return;
      this.currentIndex = (this.currentIndex - 1 + list.length) % list.length;
    },
    nextReminder() {
      const list = Array.isArray(this.reminders) ? this.reminders : [];
      if (!list.length) return;
      this.currentIndex = (this.currentIndex + 1) % list.length;
    },
    async dismissCurrent() {
      const id = this.reminderId(this.currentReminder);
      if (!id) return;
      this.busy = true;
      try {
        await this.$store.dispatch('dismissCalenderReminder', id);
      } finally {
        this.busy = false;
      }
    },
    async dismissAll() {
      const ids = (Array.isArray(this.reminders) ? this.reminders : [])
        .map((reminder) => this.reminderId(reminder))
        .filter(Boolean);
      if (!ids.length) return;
      this.busy = true;
      try {
        await Promise.all(ids.map((id) => this.$store.dispatch('dismissCalenderReminder', id)));
        await this.$store.dispatch('getDueCalenderReminders');
      } finally {
        this.busy = false;
      }
    },
    async snoozeCurrent() {
      const id = this.reminderId(this.currentReminder);
      if (!id) return;
      this.busy = true;
      try {
        await this.$store.dispatch('snoozeCalenderReminder', {
          id,
          minutes: this.selectedSnoozeMinutes,
        });
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.reminder-card {
  border-top: 4px solid #f97316;
}

.reminder-title {
  word-break: break-word;
}

.reminder-note {
  white-space: pre-wrap;
}
</style>
